import React from 'react'
import { FormField, FormMultiSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { RoleDto } from 'api/Identity/Types/Role'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface AddRoleFormValues {
  name: string
  permissionIds: number[]
}

const AddEditRoleFormUI: React.FC<FormikProps<AddRoleFormValues> & OuterProps> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors, values, setFieldValue, onClose } =
    props

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{props.role ? 'Edit Role' : 'Add Role'}</DialogTitle>
        </DialogHeader>
        <Form>
          <FormField name='name' label='Role' dataTest='role-name-field' error={errors.name} />
          <FormMultiSelectField
            name='permissionIds'
            label='Permissions'
            options={props.permissions.map((permission) => ({
              id: permission.id,
              name: permission.name,
            }))}
            selectionSummaryThreshold={10}
            value={values.permissionIds}
            error={errors.permissionIds}
            showSearch
            allowClear
            dataTest='roles-permissions-field'
            onChange={(value) => {
              const nextValue = Array.isArray(value)
                ? value.map((item) => Number(item))
                : value == null
                  ? []
                  : [Number(value)]

              setFieldValue('permissionIds', nextValue)
            }}
          />
        </Form>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button disabled={isSubmitting} onClick={handleSubmitForm} type='button'>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  role: RoleDto | undefined
  permissions: PermissionDto[]
  onSubmit(values: FormSubmitValues<AddRoleFormValues>): Promise<void>
}

export const AddRoleForm = withFormik<OuterProps, AddRoleFormValues>({
  mapPropsToValues: (props) => {
    return {
      name: props.role?.name || '',
      permissionIds: props.role?.permissions.map((x) => x.id) || [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddRoleFormValues> = {}
    if (!values.name) {
      errors.name = 'required'
    }
    if (!values.permissionIds.length) {
      errors.permissionIds = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(AddEditRoleFormUI)
